import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { reportsService } from '../../../services/reports.service'
import type {
  SalesReportFilterOptions,
  SalesReportFilters,
  SalesReportResult,
  SalesReportType,
} from '../../../services/reports.service'
import { CatalogSelect } from '../../ui/CatalogSelect'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Checkbox } from '../../ui/checkbox'
import { GlobalPaginator } from '../../ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'

const REPORT_TABS: Array<{ value: SalesReportType; label: string }> = [
  { value: 'general', label: 'Ventas generales' },
  { value: 'department-category', label: 'Departamento y categoría' },
  { value: 'products', label: 'Productos' },
  { value: 'profits', label: 'Utilidades' },
  { value: 'users', label: 'Usuario' },
  { value: 'sellers', label: 'Vendedor' },
  { value: 'customers', label: 'Clientes' },
]

const DEFAULT_FILTERS: Record<SalesReportType, SalesReportFilters> = {
  general: { currency: 'MXN', with_taxes: true },
  'department-category': { include_returns: false },
  products: { group_average: false },
  profits: { document: 'sales' },
  users: { include_returns: false, sort_by: 'sales' },
  sellers: { include_returns: false },
  customers: { discount: 'all' },
}

type Row = Record<string, string | number | null>

interface Column {
  key: string
  label: string
  kind?: 'money' | 'percentage' | 'quantity' | 'integer' | 'date' | 'date-time'
}

const REPORT_TABLE_HEAD_CLASS =
  'h-auto px-6 py-3 text-xs font-medium uppercase tracking-wider text-table-header-foreground'
const REPORT_TABLE_CELL_CLASS = 'px-6 py-4 text-table-foreground'

const COLUMNS: Record<SalesReportType, Column[]> = {
  general: [
    { key: 'folio', label: 'Serie/Folio' },
    { key: 'sale_date', label: 'Fecha de venta', kind: 'date-time' },
    { key: 'branch', label: 'Sucursal' },
    { key: 'user', label: 'Usuario' },
    { key: 'user_account', label: 'Cuenta del usuario' },
    { key: 'seller', label: 'Vendedor' },
    { key: 'seller_account', label: 'Cuenta del vendedor' },
    { key: 'cancelled_by', label: 'Usuario de cancelación' },
    { key: 'cancelled_by_account', label: 'Cuenta del usuario de cancelación' },
    { key: 'cancelled_at', label: 'Fecha de cancelación', kind: 'date-time' },
    { key: 'discount', label: 'Descuento', kind: 'money' },
    { key: 'gross_amount', label: 'Importe', kind: 'money' },
    { key: 'net_total', label: 'Total', kind: 'money' },
  ],
  'department-category': [
    { key: 'folio', label: 'Serie/Folio' },
    { key: 'branch', label: 'Sucursal' },
    { key: 'user', label: 'Usuario' },
    { key: 'seller', label: 'Vendedor' },
    { key: 'department', label: 'Departamento' },
    { key: 'category', label: 'Categoría' },
    { key: 'discount', label: 'Descuento', kind: 'money' },
    { key: 'gross_amount', label: 'Importe', kind: 'money' },
    { key: 'net_total', label: 'Total', kind: 'money' },
  ],
  products: [
    { key: 'product', label: 'Producto' },
    { key: 'barcode', label: 'Código de barras' },
    { key: 'department', label: 'Departamento' },
    { key: 'category', label: 'Categoría' },
    { key: 'quantity', label: 'Cantidad', kind: 'quantity' },
    { key: 'average_price', label: 'Precio con impuestos', kind: 'money' },
    { key: 'total_amount', label: 'Importe con impuestos', kind: 'money' },
  ],
  profits: [
    { key: 'folio', label: 'Serie/Folio' },
    { key: 'sale_date', label: 'Fecha de venta', kind: 'date-time' },
    { key: 'status_label', label: 'Estado' },
    { key: 'branch', label: 'Sucursal' },
    { key: 'user', label: 'Usuario' },
    { key: 'user_account', label: 'Cuenta del usuario' },
    { key: 'seller', label: 'Vendedor' },
    { key: 'seller_account', label: 'Cuenta del vendedor' },
    { key: 'cancelled_by', label: 'Usuario de cancelación' },
    { key: 'cancelled_by_account', label: 'Cuenta del usuario de cancelación' },
    { key: 'cancelled_at', label: 'Fecha de cancelación', kind: 'date-time' },
    { key: 'customer', label: 'Cliente' },
    { key: 'discount', label: 'Descuento', kind: 'money' },
    { key: 'net_revenue', label: 'Importe', kind: 'money' },
    { key: 'cost', label: 'Costo', kind: 'money' },
    { key: 'profit', label: 'Utilidad', kind: 'money' },
    { key: 'margin_percentage', label: 'Margen sobre venta %', kind: 'percentage' },
    { key: 'markup_percentage', label: 'Rendimiento sobre costo %', kind: 'percentage' },
  ],
  users: peopleColumns('Usuario'),
  sellers: peopleColumns('Vendedor'),
  customers: [
    { key: 'name', label: 'Cliente' },
    { key: 'sales_count', label: 'No. Ventas' },
    { key: 'average', label: 'Ticket Promedio', kind: 'money' },
    { key: 'discount_percentage', label: 'Descuento %', kind: 'percentage' },
    { key: 'net_total', label: 'Importe con Impuestos', kind: 'money' },
  ],
}

function peopleColumns(firstLabel: string): Column[] {
  return [
    { key: 'name', label: firstLabel },
    { key: 'sales_count', label: 'No. Ventas' },
    { key: 'cancelled_count', label: 'No. Ventas Canceladas' },
    { key: 'returns_count', label: 'Devoluciones' },
    { key: 'cancelled_returns_count', label: 'Dev. Canceladas' },
    { key: 'average', label: 'Promedio', kind: 'money' },
    { key: 'discount_percentage', label: 'Descuento %', kind: 'percentage' },
    { key: 'net_total', label: 'Importe Total', kind: 'money' },
  ]
}

export interface AppliedSalesReportContext {
  type: SalesReportType
  filters: SalesReportFilters
}

interface VentasTabProps {
  activeReport: SalesReportType
  onActiveReportChange: (type: SalesReportType) => void
  onAppliedContextChange: (context: AppliedSalesReportContext) => void
}

export function VentasTab({
  activeReport,
  onActiveReportChange,
  onAppliedContextChange,
}: VentasTabProps) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [pages, setPages] = useState<Record<SalesReportType, number>>({
    general: 1,
    'department-category': 1,
    products: 1,
    profits: 1,
    users: 1,
    sellers: 1,
    customers: 1,
  })

  const optionsQuery = useQuery({
    queryKey: ['reports', 'sales', 'filter-options'],
    queryFn: () => reportsService.getSalesFilterOptions(),
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    onAppliedContextChange({
      type: activeReport,
      filters: filters[activeReport],
    })
  }, [activeReport, filters, onAppliedContextChange])

  const commonProps: SalesSubreportProps = {
    filters: filters[activeReport],
    page: pages[activeReport],
    options: optionsQuery.data,
    optionsLoading: optionsQuery.isLoading,
    onFiltersChange: (nextFilters) => {
      setFilters((current) => ({ ...current, [activeReport]: nextFilters }))
      setPages((current) => ({ ...current, [activeReport]: 1 }))
    },
    onClearFilters: () => {
      setFilters((current) => ({ ...current, [activeReport]: DEFAULT_FILTERS[activeReport] }))
      setPages((current) => ({ ...current, [activeReport]: 1 }))
    },
    onPageChange: (page) => {
      setPages((current) => ({ ...current, [activeReport]: page }))
    },
  }

  const report = (() => {
    switch (activeReport) {
      case 'general': return <GeneralSalesReport {...commonProps} />
      case 'department-category': return <DepartmentCategorySalesReport {...commonProps} />
      case 'products': return <ProductsSalesReport {...commonProps} />
      case 'profits': return <ProfitsSalesReport {...commonProps} />
      case 'users': return <UsersSalesReport {...commonProps} />
      case 'sellers': return <SellersSalesReport {...commonProps} />
      case 'customers': return <CustomersSalesReport {...commonProps} />
    }
  })()

  const handleReportChange = (value: string) => {
    const nextReport = value as SalesReportType
    if (nextReport === activeReport) return

    setFilters((current) => ({
      ...current,
      [activeReport]: DEFAULT_FILTERS[activeReport],
      [nextReport]: DEFAULT_FILTERS[nextReport],
    }))
    setPages((current) => ({
      ...current,
      [activeReport]: 1,
      [nextReport]: 1,
    }))
    onActiveReportChange(nextReport)
  }

  return (
    <TabsContent value="ventas" className="space-y-5">
      <Tabs
        value={activeReport}
        onValueChange={handleReportChange}
        className="space-y-5"
      >
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
          {REPORT_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="text-xs sm:text-sm">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {report}
      </Tabs>
    </TabsContent>
  )
}

interface SalesSubreportProps {
  filters: SalesReportFilters
  page: number
  options?: SalesReportFilterOptions
  optionsLoading: boolean
  onFiltersChange: (filters: SalesReportFilters) => void
  onClearFilters: () => void
  onPageChange: (page: number) => void
}

function GeneralSalesReport(props: SalesSubreportProps) {
  return <SalesReportPanel {...props} type="general" />
}

function DepartmentCategorySalesReport(props: SalesSubreportProps) {
  return <SalesReportPanel {...props} type="department-category" />
}

function ProductsSalesReport(props: SalesSubreportProps) {
  return <SalesReportPanel {...props} type="products" />
}

function ProfitsSalesReport(props: SalesSubreportProps) {
  return <SalesReportPanel {...props} type="profits" />
}

function UsersSalesReport(props: SalesSubreportProps) {
  return <SalesReportPanel {...props} type="users" />
}

function SellersSalesReport(props: SalesSubreportProps) {
  return <SalesReportPanel {...props} type="sellers" />
}

function CustomersSalesReport(props: SalesSubreportProps) {
  return <SalesReportPanel {...props} type="customers" />
}

function SalesReportPanel({
  type,
  filters,
  page,
  options,
  optionsLoading,
  onFiltersChange,
  onClearFilters,
  onPageChange,
}: SalesSubreportProps & { type: SalesReportType }) {
  const queryFilters = useMemo(
    () => ({ ...filters, page }),
    [filters, page],
  )
  const reportQuery = useQuery({
    queryKey: ['reports', 'sales', type, queryFilters],
    queryFn: () => reportsService.getSalesReport<Row>(type, queryFilters),
    placeholderData: (previous) => previous,
  })

  return (
    <TabsContent value={type} className="space-y-5">
      <GlobalFilterBar
        show={['dates']}
        values={{
          startDate: String(filters.date_from ?? ''),
          endDate: String(filters.date_to ?? ''),
        }}
        onChange={(changes) => {
          const nextFilters = { ...filters }
          if (changes.startDate !== undefined) {
            nextFilters.date_from = changes.startDate || undefined
          }
          if (changes.endDate !== undefined) {
            nextFilters.date_to = changes.endDate || undefined
          }
          onFiltersChange(nextFilters)
        }}
        onClearFilters={onClearFilters}
        hasActiveFilters={hasActiveFilters(type, filters)}
      >
        <ReportSpecificFilters
          type={type}
          filters={filters}
          options={options}
          onChange={onFiltersChange}
        />
        {optionsLoading && (
          <span className="flex items-center gap-1 self-center text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Catálogos
          </span>
        )}
      </GlobalFilterBar>

      <SalesResults
        type={type}
        result={reportQuery.data}
        isLoading={reportQuery.isLoading}
        isFetching={reportQuery.isFetching}
        error={reportQuery.error}
        onPageChange={onPageChange}
      />
    </TabsContent>
  )
}

function ReportSpecificFilters({
  type,
  filters,
  options,
  onChange,
}: {
  type: SalesReportType
  filters: SalesReportFilters
  options?: SalesReportFilterOptions
  onChange: (filters: SalesReportFilters) => void
}) {
  if (type === 'general') {
    return (
      <>
        <OptionsSelect label="Sucursal" value={filters.sucursal_id} options={options?.sucursals} onChange={(value) => onChange({ ...filters, sucursal_id: numberOrUndefined(value) })} />
        <OptionsSelect label="Estado" value={filters.status} options={options?.statuses} onChange={(value) => onChange({ ...filters, status: optionalValue(value) })} />
        <OptionsSelect label="Método de pago" value={filters.payment_method_id} options={options?.payment_methods} onChange={(value) => onChange({ ...filters, payment_method_id: numberOrUndefined(value) })} />
        <BooleanFilter label="Mostrar cantidades con IVA" checked={filters.with_taxes !== false} onChange={(checked) => onChange({ ...filters, with_taxes: checked })} />
      </>
    )
  }

  if (type === 'department-category') {
    return (
      <>
        <OptionsSelect label="Sucursal" value={filters.sucursal_id} options={options?.sucursals} onChange={(value) => onChange({ ...filters, sucursal_id: numberOrUndefined(value) })} />
        <OptionsSelect label="Departamento" value={filters.department_id} options={options?.departments} onChange={(value) => onChange({ ...filters, department_id: numberOrUndefined(value) })} />
        <OptionsSelect label="Categoría" value={filters.category_id} options={options?.categories} onChange={(value) => onChange({ ...filters, category_id: numberOrUndefined(value) })} />
        <BooleanFilter label="Incluir devoluciones" checked={Boolean(filters.include_returns)} onChange={(checked) => onChange({ ...filters, include_returns: checked })} />
      </>
    )
  }

  if (type === 'products') {
    return (
      <>
        <Field label="Producto">
          <CatalogSelect catalog="report-products" placeholder="Todos los productos" value={filters.product_id} onChange={(value) => onChange({ ...filters, product_id: numberOrUndefined(String(value)) })} />
        </Field>
        <BooleanFilter label="Agrupar y promediar" checked={Boolean(filters.group_average)} onChange={(checked) => onChange({ ...filters, group_average: checked })} />
      </>
    )
  }

  if (type === 'profits') {
    return (
      <>
        <OptionsSelect label="Sucursal" value={filters.sucursal_id} options={options?.sucursals} onChange={(value) => onChange({ ...filters, sucursal_id: numberOrUndefined(value) })} />
        <OptionsSelect label="Documento" value={filters.document ?? 'sales'} options={options?.documents} allowAll={false} onChange={() => onChange({ ...filters, document: 'sales' })} />
      </>
    )
  }

  if (type === 'users') {
    return (
      <>
        <OptionsSelect label="Ordenar por" value={filters.sort_by ?? 'sales'} options={options?.sort_options} allowAll={false} onChange={(value) => onChange({ ...filters, sort_by: value as SalesReportFilters['sort_by'] })} />
        <BooleanFilter label="Ver devoluciones de ventas" checked={Boolean(filters.include_returns)} onChange={(checked) => onChange({ ...filters, include_returns: checked })} />
      </>
    )
  }

  if (type === 'sellers') {
    return <BooleanFilter label="Ver devoluciones de ventas" checked={Boolean(filters.include_returns)} onChange={(checked) => onChange({ ...filters, include_returns: checked })} />
  }

  return (
    <>
      <Field label="Cliente">
        <CatalogSelect catalog="report-customers" placeholder="Todos los clientes" value={filters.customer_id} onChange={(value) => onChange({ ...filters, customer_id: numberOrUndefined(String(value)) })} />
      </Field>
      <OptionsSelect
        label="Descuento"
        value={filters.discount ?? 'all'}
        allowAll={false}
        options={[
          { value: 'all', label: 'Todos' },
          { value: 'with', label: 'Con descuento' },
          { value: 'without', label: 'Sin descuento' },
        ]}
        onChange={(value) => onChange({ ...filters, discount: value as SalesReportFilters['discount'] })}
      />
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

function OptionsSelect({
  label,
  value,
  options = [],
  allowAll = true,
  onChange,
}: {
  label: string
  value: unknown
  options?: Array<{ id?: number; value?: string; name?: string; label?: string }>
  allowAll?: boolean
  onChange: (value: string) => void
}) {
  const selected = value === undefined || value === null || value === '' ? 'all' : String(value)

  return (
    <Field label={label}>
      <Select value={selected} onValueChange={onChange}>
        <SelectTrigger aria-label={label}><SelectValue placeholder="Todos" /></SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="all">Todos</SelectItem>}
          {options.map((option) => {
            const optionValue = String(option.id ?? option.value)
            return <SelectItem key={optionValue} value={optionValue}>{option.name ?? option.label ?? optionValue}</SelectItem>
          })}
        </SelectContent>
      </Select>
    </Field>
  )
}

function BooleanFilter({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div className="flex min-h-10 items-end pb-2">
      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
        <Checkbox checked={checked} onCheckedChange={(value) => onChange(value === true)} />
        {label}
      </label>
    </div>
  )
}

function SalesResults({
  type,
  result,
  isLoading,
  isFetching,
  error,
  onPageChange,
}: {
  type: SalesReportType
  result?: SalesReportResult<Row>
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  onPageChange: (page: number) => void
}) {
  const columns = result?.columns?.length
    ? result.columns.map((column): Column => ({
        key: column.key,
        label: column.label,
        kind: reportColumnKind(column.type),
      }))
    : COLUMNS[type]

  if (isLoading) {
    return (
      <TableSkeleton
        headers={columns.map((column) => column.label)}
        columns={columns.map(() => 'text' as const)}
        rows={10}
      />
    )
  }

  if (error) {
    return <ReportState icon={<AlertCircle className="h-6 w-6 text-red-500" />} message="No fue posible cargar el reporte. Intenta nuevamente." />
  }

  if (!result || result.rows.length === 0) {
    return <ReportState message="No hay registros para los filtros aplicados." />
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h3 className="font-semibold text-table-foreground">Resultados</h3>
          <p className="text-xs text-muted-foreground">{result.pagination.total} registros filtrados</p>
        </div>
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      <Table className="min-w-max">
        <TableHeader className="border-b border-border bg-table-header-bg text-table-header-foreground">
          <TableRow className="hover:bg-transparent">
            {columns.map((column) => <TableHead key={column.key} className={`${REPORT_TABLE_HEAD_CLASS} ${isNumeric(column) ? 'text-right' : ''}`}>{column.label}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody className="[&_tr]:border-border">
          {result.rows.map((row, index) => (
            <TableRow key={`${type}-${result.pagination.current_page}-${index}`} className="hover:bg-table-row-hover">
              {columns.map((column) => (
                <TableCell key={column.key} className={`${REPORT_TABLE_CELL_CLASS} ${isNumeric(column) ? 'text-right font-medium tabular-nums' : ''}`}>
                  {formatCell(row[column.key], column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter className="border-t border-border bg-table-header-bg font-semibold text-table-foreground">
          <TableRow className="hover:bg-transparent">
            {columns.map((column, index) => (
              <TableCell key={column.key} className={`${REPORT_TABLE_CELL_CLASS} font-semibold ${isNumeric(column) ? 'text-right tabular-nums' : ''}`}>
                {index === 0 ? 'TOTAL GENERAL' : formatTotal(result.totals[column.key], column)}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>

      <GlobalPaginator
        totalItems={result.pagination.total}
        itemsPerPage={result.pagination.per_page}
        currentPage={result.pagination.current_page}
        from={result.pagination.from ?? 0}
        to={result.pagination.to ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function ReportState({ icon, message }: { icon?: React.ReactNode; message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {icon}
      <span>{message}</span>
    </div>
  )
}

function hasActiveFilters(type: SalesReportType, filters: SalesReportFilters) {
  const defaults = DEFAULT_FILTERS[type]
  const keys = new Set([...Object.keys(defaults), ...Object.keys(filters)])

  return Array.from(keys).some((key) => filters[key] !== defaults[key])
}

function isNumeric(column: Column) {
  return ['money', 'percentage', 'quantity', 'integer'].includes(column.kind ?? '') || column.key.endsWith('_count')
}

function formatCell(value: string | number | null | undefined, column: Column) {
  if (value === null || value === undefined) return column.kind === 'money' || column.kind === 'percentage' ? 'No disponible' : '—'
  if (column.kind === 'money') return formatMoney(value)
  if (column.kind === 'percentage') return `${Number(value).toFixed(2)}%`
  if (column.kind === 'quantity') return Number(value).toLocaleString('es-MX', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
  if (column.kind === 'integer') return Number(value).toLocaleString('es-MX', { maximumFractionDigits: 0 })
  if (column.kind === 'date' || column.kind === 'date-time') return formatReportDate(String(value), column.kind === 'date-time')
  return String(value)
}

function formatTotal(value: string | number | null | undefined, column: Column) {
  if (value === undefined) return ''
  return formatCell(value, column)
}

function formatMoney(value: string | number) {
  return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

function reportColumnKind(type: NonNullable<SalesReportResult<Row>['columns']>[number]['type']): Column['kind'] {
  if (type === 'text') return undefined
  if (type === 'decimal') return 'quantity'
  return type
}

function formatReportDate(value: string, withTime: boolean) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/)
  if (!match) return value

  const [, year, month, day, hours = '00', minutes = '00'] = match
  return withTime ? `${day}/${month}/${year} ${hours}:${minutes}` : `${day}/${month}/${year}`
}

function optionalValue(value: string) {
  return value === 'all' ? undefined : value
}

function numberOrUndefined(value: string) {
  return value === 'all' || value === '' ? undefined : Number(value)
}
